import React, { useState, useMemo } from "react";
import { Mail, RefreshCw, Send, PlayCircle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { StaffShell } from "@/components/shells";
import { Panel, Spinner, Tag, EmptyState, Btn, Select, Field } from "@/components/kit";
import { useAsync } from "@/lib/hooks";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/vda";

const STATUS_TONE = { sent: "green", queued: "muted", sending: "muted", failed: "red", simulated: "clay", skipped: "muted", suppressed: "muted" };
const TABS = [
  { key: "", label: "All" },
  { key: "queued", label: "Pending" },
  { key: "sent", label: "Sent" },
  { key: "failed", label: "Failed" },
  { key: "simulated", label: "Simulated" },
  { key: "suppressed", label: "Suppressed" },
];
const MODE_LABEL = { none: "Off", daily_summary: "Daily summary", critical_only: "Critical only" };

export default function Notifications() {
  const { profile } = useAuth();
  const role = profile?.role;
  const canManage = role === "administrator" || role === "account_manager";

  const [tab, setTab] = useState("");
  const { data, loading, reload } = useAsync(async () => {
    const { data, error } = await supabase
      .from("notifications")
      .select("*, booking:booking_request_id(human_reference)")
      .order("created_at", { ascending: false })
      .limit(300);
    if (error) throw error;
    return data || [];
  }, []);

  const customers = useAsync(async () => {
    const { data, error } = await supabase.from("customers").select("id,name,external_notify_mode").order("name");
    if (error) return [];
    return data || [];
  }, []);

  // ---- test digest + external summary controls ----
  const [kind, setKind] = useState("internal");
  const [testCustomer, setTestCustomer] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [resending, setResending] = useState(null);

  const sendTest = async () => {
    if (kind === "external" && !testCustomer) return toast.error("Pick a customer for the external preview");
    setTestBusy(true); setPreview(null);
    try {
      const r = await api.adminDigestTest({ kind, customer_id: kind === "external" ? testCustomer : undefined });
      setPreview(r.preview);
      toast.success(`Test ${kind} digest ${r.status}.`);
      reload();
    } catch (e) { toast.error(e.message || "Test failed"); }
    finally { setTestBusy(false); }
  };

  const runSummary = async () => {
    setSummaryBusy(true);
    try {
      const r = await api.adminExternalSummaryRun();
      if (r.status === "ran") toast.success(`External summaries sent: ${r.summaries_sent} · skipped: ${r.skipped}.`);
      else toast.message(`Summary ${r.status}${r.reason ? ` — ${r.reason}` : ""}.`);
      reload();
    } catch (e) { toast.error(e.message || "Run failed"); }
    finally { setSummaryBusy(false); }
  };

  const setMode = async (cid, mode) => {
    try {
      await api.adminSetNotifyMode(cid, mode);
      toast.success(`Updated to ${MODE_LABEL[mode]}.`);
      customers.reload();
    } catch (e) { toast.error(e.message || "Could not update"); }
  };

  const resend = async (id) => {
    setResending(id);
    try {
      await api.resendNotification(id);
      toast.success("Resent.");
      reload();
    } catch (e) { toast.error(e.message || "Resend failed"); }
    finally { setResending(null); }
  };

  const rows = useMemo(() => (data || []).filter((n) => (tab ? n.status === tab : true)), [data, tab]);

  if (loading) return <StaffShell><Spinner /></StaffShell>;

  return (
    <StaffShell>
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="vda-tick text-[hsl(var(--vda-clay))]">Communications</p>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Email &amp; notifications</h1>
        </div>
        <button onClick={reload} className="text-sm text-muted-foreground inline-flex items-center gap-1" data-testid="notif-refresh"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </div>

      {canManage && (
        <div className="grid lg:grid-cols-2 gap-5 mb-5">
          <Panel className="p-5" data-testid="test-digest-panel">
            <h2 className="font-semibold mb-1">Test &amp; delivery controls</h2>
            <p className="text-sm text-muted-foreground mb-4">Send a labelled <strong>[TEST]</strong> digest to your own email. Live scheduled digests and dedupe state are never affected.</p>
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-[160px]">
                <Field label="Digest kind">
                  <Select value={kind} onChange={(e) => { setKind(e.target.value); setPreview(null); }} data-testid="test-kind-select">
                    <option value="internal">Internal overdue SLA</option>
                    <option value="external">External customer summary</option>
                    <option value="critical">Critical event</option>
                  </Select>
                </Field>
              </div>
              {kind === "external" && (
                <div className="min-w-[180px]">
                  <Field label="Customer">
                    <Select value={testCustomer} onChange={(e) => setTestCustomer(e.target.value)} data-testid="test-customer-select">
                      <option value="">Select customer…</option>
                      {(customers.data || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </Select>
                  </Field>
                </div>
              )}
              <Btn onClick={sendTest} loading={testBusy} data-testid="send-test-btn"><Send className="h-4 w-4" /> Send test to me</Btn>
              <Btn variant="outline" onClick={runSummary} loading={summaryBusy} data-testid="run-summary-btn"><PlayCircle className="h-4 w-4" /> Run external summary now</Btn>
            </div>
            {preview && (
              <div className="mt-4 rounded-md border border-border bg-secondary/40 p-3 text-sm" data-testid="test-preview">
                <div className="font-medium">{preview.subject}</div>
                <div className="mt-1 text-muted-foreground">To {preview.recipient} · scope: {preview.customer_scope} · {preview.bookings_included} booking(s)</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  <Tag tone={preview.delivery_mode === "LIVE" ? "green" : "clay"}>{preview.delivery_mode}</Tag>
                  {preview.sections.map((s) => <Tag key={s} tone="muted">{s}</Tag>)}
                </div>
              </div>
            )}
          </Panel>

          <Panel className="p-5" data-testid="notify-mode-panel">
            <h2 className="font-semibold mb-1">External fleet-manager notifications</h2>
            <p className="text-sm text-muted-foreground mb-4">Per-customer delivery preference for daily summaries and critical alerts.</p>
            {customers.loading ? <Spinner /> : (
              <div className="divide-y divide-border">
                {(customers.data || []).map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-3 py-2" data-testid={`notify-mode-row-${c.id}`}>
                    <span className="text-sm font-medium">{c.name}</span>
                    <div className="w-44">
                      <Select value={c.external_notify_mode || "none"} onChange={(e) => setMode(c.id, e.target.value)} data-testid={`notify-mode-select-${c.id}`}>
                        <option value="none">Off</option>
                        <option value="daily_summary">Daily summary</option>
                        <option value="critical_only">Critical only</option>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4" data-testid="notif-tabs">
        {TABS.map((t) => (
          <button key={t.key || "all"} data-testid={`notif-tab-${t.key || "all"}`} onClick={() => setTab(t.key)}
            aria-pressed={tab === t.key}
            className={`px-3 h-8 inline-flex items-center text-xs font-medium rounded-full border transition-colors ${tab === t.key ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <Panel className="p-0 overflow-hidden" data-testid="notifications-panel">
        {rows.length === 0 ? <div className="p-5"><EmptyState title="No notifications" hint="Operational emails will appear here once triggered." /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead>
                <tr className="bg-secondary/60 text-left vda-tick text-muted-foreground">
                  <th className="px-4 py-2 font-medium">When</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Booking</th>
                  <th className="px-4 py-2 font-medium">Recipient</th>
                  <th className="px-4 py-2 font-medium">Retries</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((n) => (
                  <tr key={n.id} className="border-t border-border align-top" data-testid={`notif-row-${n.id}`}>
                    <td className="px-4 py-2 tabular-nums whitespace-nowrap">{fmtDateTime(n.created_at)}</td>
                    <td className="px-4 py-2">{(n.notification_type || "").replace(/_/g, " ")}</td>
                    <td className="px-4 py-2 vda-reg">{n.booking?.human_reference || "—"}</td>
                    <td className="px-4 py-2"><Tag tone="muted">{(n.recipient_type || "").replace(/_/g, " ")}</Tag></td>
                    <td className="px-4 py-2 tabular-nums">{n.retry_count ?? 0}</td>
                    <td className="px-4 py-2">
                      <Tag tone={STATUS_TONE[n.status] || "muted"}>{n.status}</Tag>
                      {n.suppression_reason && <div className="mt-1 text-xs text-muted-foreground max-w-[180px]" data-testid={`notif-suppression-${n.id}`}>{n.suppression_reason}</div>}
                    </td>
                    <td className="px-4 py-2">
                      {n.status === "failed" && (
                        <Btn variant="outline" onClick={() => resend(n.id)} loading={resending === n.id} data-testid={`resend-btn-${n.id}`}>
                          <RotateCcw className="h-3.5 w-3.5" /> Resend
                        </Btn>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <p className="mt-3 text-xs text-muted-foreground"><Mail className="h-3 w-3 inline" /> Authentication emails (sign-in codes / magic links) are handled by Supabase Auth and never appear here.</p>
    </StaffShell>
  );
}
